import type { InputRef } from 'antd';
import React, { useState, useRef, useEffect } from 'react';
import { Input, Dropdown, Avatar, message, Button } from 'antd';
import { SearchOutlined, UserOutlined, MenuOutlined, LogoutOutlined, LoadingOutlined, CloseOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';
import styled from 'styled-components';
import { useAuthStore } from '@/stores/auth';
import { useChainStore } from '@/stores/chain';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoFull from '@assets/icon/logo-full.svg?react';
import { searchAPI, SearchResult, SearchResultTransaction, SearchResultCommitment } from '@/api/search';
import { useIsMobile } from '@packet/management';

// Styled Components
const HeaderContainer = styled.header`
  height: var(--pc-header-height);
  background: var(--white);

  padding: 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  gap: 20px;
`;

const HeaderContainerWrapper = styled.div`
  max-width: var(--pc-max-width);
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// 左侧区域：Logo + 导航
const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  height: 72px;
`;

const Divider = styled.div`
  width: 1px;
  height: 32px;
  background-color: var(--color-text-01);
  margin: 0 24px;
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: bold;
  @media (max-width: 768px) {
    svg {
      width: 126px;
    }
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 768px) {
    display: none;
  }
`;

// 移动端三横杠icon样式
const MobileMenuIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(243, 245, 242, 0.5);
  cursor: pointer;
`;

const NavItem = styled.div`
  position: relative;
`;

const NavLink = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text);
  cursor: pointer;
  padding: 8px 0;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 2px;
  background-color: var(--color-text);
`;

// 中间区域：搜索框
const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
  max-width: 428px;
  margin: 0 auto;

  @media (max-width: 1024px) {
    max-width: 300px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchContainer = styled.div`
  width: 100%;
`;

const StyledSearchInput = styled(Input)`
  height: 44px;
  border-radius: 60px;
  background-color: rgba(243, 245, 242, 0.5);
  border: none;
  font-size: 16px;
  width: 428px;
  padding: 12px 14px;
  margin-right: 14px;

  > input::placeholder {
    color: var(--color-text-04);
  }

  &.ant-input-affix-wrapper {
    background-color: rgba(243, 245, 242, 0.5);
    border: none;

    &:hover,
    &:focus {
      background-color: rgba(243, 245, 242, 0.7);
      border: none;
      box-shadow: none;
    }
  }
`;

// 移动端搜索相关样式
const MobileSearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(243, 245, 242, 0.5);
  cursor: pointer;
`;

const MobileSearchMask = styled.div<{ $visible: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $visible }) => ($visible ? 'block' : 'none')};
    position: fixed;
    z-index: 2000;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    transition: background 0.2s;
  }
`;

const MobileSearchPopup = styled.div<{ $visible: boolean }>`
  display: none;
  @media (max-width: 768px) {
    display: ${({ $visible }) => ($visible ? 'flex' : 'none')};
    position: fixed;
    z-index: 2100;
    top: 0;
    left: 0;
    right: 0;
    height: 64px;
    background: #fff;
    align-items: center;
    padding: 0 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    gap: 8px;
  }
`;

const MobileSearchInput = styled(Input)`
  flex: 1;
  height: 40px;
  border-radius: 20px;
  background: rgba(243, 245, 242, 0.7);
  border: none;
  font-size: 16px;
  padding: 0 14px;
  &.ant-input-affix-wrapper {
    background: rgba(243, 245, 242, 0.7);
    border: none;
    box-shadow: none;
  }
`;

// 右侧区域：用户配置文件
const RightSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const UserProfileContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 60px;
  padding: 10px 14px 10px 10px;
  gap: 6px;
`;

const UserName = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text);
  cursor: pointer;
  transition: opacity 0.2s ease;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }
`;

const StyledAvatar = styled(Avatar)`
  background-color: var(--color-text) !important;
`;

const DrawerItem = styled.div`
  font-size: 18px;
  border-bottom: 1px solid var(--color-text-01);
  font-weight: 500;
  color: var(--color-text);
  cursor: pointer;
  line-height: 40px;
  text-align: center;
`;

export default function Header() {
  const { logout, user } = useAuthStore();
  const { chainId } = useChainStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { isMobile } = useIsMobile();

  // 移动端搜索弹窗
  const [mobileSearchVisible, setMobileSearchVisible] = useState(false);
  const mobileInputRef = useRef<InputRef>(null);

  // 移动端 Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 打开移动端搜索弹窗时自动聚焦
  useEffect(() => {
    if (mobileSearchVisible && mobileInputRef.current) {
      setTimeout(() => {
        mobileInputRef.current?.focus?.();
      }, 100);
    }
  }, [mobileSearchVisible]);

  // 监听 ESC 键关闭
  useEffect(() => {
    if (!mobileSearchVisible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileSearchVisible(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mobileSearchVisible]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    // {
    //   key: 'profile',
    //   icon: <UserOutlined />,
    //   label: 'profile',
    //   onClick: () => {
    //     // TODO: 跳转到个人资料页面
    //   },
    // },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'logout',
      onClick: handleLogout,
    },
  ];

  const navItems = [
    {
      key: '/transactions',
      label: 'Transaction',
      activeStartsWith: '/transaction',
    },
    {
      key: '/utxo',
      label: 'UTXO',
      activeStartsWith: '/utxo',
    },
  ];

  const handleNavClick = (path: string) => {
    // 获取当前URL的查询参数
    const searchParams = new URLSearchParams(location.search);
    const chainId = searchParams.get('chain_id');

    // 如果存在chain_id参数，则在跳转时保持该参数
    const newSearchParams = new URLSearchParams();
    if (chainId) {
      newSearchParams.set('chain_id', chainId);
    }

    // 构建新的URL路径，包含查询参数
    const searchString = newSearchParams.toString();
    const fullPath = searchString ? `${path}?${searchString}` : path;

    navigate(fullPath);
  };

  const handleSearch = async (value: string) => {
    if (!value.trim()) {
      message.warning('请输入搜索内容');
      return;
    }

    setIsSearching(true);

    try {
      const response = await searchAPI({
        query: value.trim(),
        limit: 10,
      });

      if (response && response.length > 0) {
        const firstResult = response[0];

        if (firstResult.type === 'transaction') {
          // 跳转到交易详情页面
          const transactionResult = firstResult as SearchResultTransaction;
          navigate(`/transaction/${transactionResult.chain_id}/${transactionResult.txhash}-${transactionResult.tx_index}`);
        } else if (firstResult.type === 'commitment') {
          // 跳转到UTXO详情页面
          const commitmentResult = firstResult as SearchResultCommitment;
          navigate(`/utxo/${commitmentResult.chain_id}/${commitmentResult.commitment}`);
        }

        // 清空搜索框
        setSearchValue('');
      } else {
        message.error('Your search did not match any records');
      }
    } catch (error) {
      console.error('搜索失败:', error);
      message.error('Your search has encountered an error.');
    } finally {
      setIsSearching(false);
    }
  };

  // 处理粘贴事件
  const handlePaste = async (e: React.ClipboardEvent<HTMLInputElement>) => {
    // 等待粘贴内容更新到输入框
    setTimeout(() => {
      const pastedValue = (e.target as HTMLInputElement).value;
      if (pastedValue.trim()) {
        handleSearch(pastedValue.trim());
      }
    }, 100);
  };

  return (
    <HeaderContainer>
      <HeaderContainerWrapper>
        {/* 左侧区域：Logo + 导航 */}
        <LeftSection>
          <LogoContainer>
            <LogoWrapper>
              <LogoFull onClick={() => navigate('/')} />
            </LogoWrapper>
          </LogoContainer>

          {!isMobile && <Divider></Divider>}

          <NavigationContainer>
            {navItems.map((item) => (
              <NavItem key={item.key}>
                <NavLink onClick={() => handleNavClick(item.key)}>{item.label}</NavLink>
                {location.pathname.includes(item.activeStartsWith) && <ActiveIndicator />}
              </NavItem>
            ))}
          </NavigationContainer>
        </LeftSection>

        {/* 中间区 */}
        <CenterSection></CenterSection>

        {/* 右侧区域 */}
        <RightSection>
          {/* PC端搜索框 */}
          {!isMobile && (
            <SearchContainer>
              <StyledSearchInput
                placeholder="Search by UTXO ID / Txn Hash"
                prefix={isSearching ? <LoadingOutlined style={{ color: 'var(--color-text)', marginRight: '12px' }} /> : <SearchOutlined style={{ color: 'var(--color-text)', marginRight: '12px' }} />}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={() => handleSearch(searchValue)}
                onPaste={handlePaste}
                disabled={isSearching}
              />
            </SearchContainer>
          )}
          {/* 移动端搜索icon */}
          {isMobile && (
            <MobileSearchIconWrapper onClick={() => setMobileSearchVisible(true)}>
              <SearchOutlined style={{ fontSize: 22, color: 'var(--color-text)' }} />
            </MobileSearchIconWrapper>
          )}
          {/* 移动端三横杠icon */}
          {isMobile && (
            <MobileMenuIconWrapper onClick={() => setDrawerOpen(true)}>
              <MenuOutlined style={{ fontSize: 22, color: 'var(--color-text)' }} />
            </MobileMenuIconWrapper>
          )}
          {/* 移动端头像 */}
          {!isMobile && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottom">
              <UserProfileContainer>
                <StyledAvatar size={24} icon={<UserOutlined />} />
                <UserName>{user?.username || 'Name'}</UserName>
              </UserProfileContainer>
            </Dropdown>
          )}
        </RightSection>
      </HeaderContainerWrapper>
      {/* 移动端 Drawer */}
      <Drawer title={null} placement="top" closable={false} open={drawerOpen} onClose={() => setDrawerOpen(false)} height={260} style={{ borderRadius: '0 0 16px 16px' }} className="mobile-nav-drawer">
        {navItems.map((item) => (
          <DrawerItem
            key={item.key}
            style={{
              fontSize: 18,
              fontWeight: 500,
              color: location.pathname.includes(item.activeStartsWith) ? 'var(--color-text)' : '#888',
              cursor: 'pointer',
              padding: '8px 0',
            }}
            onClick={() => {
              handleNavClick(item.key);
              setDrawerOpen(false);
            }}
          >
            {item.label}
          </DrawerItem>
        ))}
        <div style={{ textAlign: 'center', lineHeight: '40px', padding: '16px 0', marginTop: 20 }}>
          <Button shape="round" onClick={handleLogout} style={{ width: '100%', height: '40px' }}>
            Log out
          </Button>
        </div>
      </Drawer>
      {/* 移动端搜索弹窗和遮罩 */}
      <MobileSearchMask $visible={mobileSearchVisible} onClick={() => setMobileSearchVisible(false)} />
      <MobileSearchPopup $visible={mobileSearchVisible}>
        <MobileSearchInput
          ref={mobileInputRef}
          placeholder="Search by UTXO ID / Txn Hash"
          prefix={isSearching ? <LoadingOutlined style={{ color: 'var(--color-text)', marginRight: '8px' }} /> : <SearchOutlined style={{ color: 'var(--color-text)', marginRight: '8px' }} />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onPressEnter={() => {
            handleSearch(searchValue);
            setMobileSearchVisible(false);
          }}
          onPaste={handlePaste}
          disabled={isSearching}
          allowClear
          style={{ minWidth: 0 }}
        />
        <CloseOutlined style={{ fontSize: 20, color: '#888', marginLeft: 8, cursor: 'pointer' }} onClick={() => setMobileSearchVisible(false)} />
      </MobileSearchPopup>
    </HeaderContainer>
  );
}
